import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, FolderOpen, FileText, ScrollText, BookOpen,
  BarChart3, Table2, Receipt, Archive, Loader2, Download,
  CheckCircle2, AlertCircle, ExternalLink, Compass, ShieldCheck,
  FileSearch, Clock, RefreshCw, Gavel, ClipboardList, Package, Shield,
} from 'lucide-react';
import AttorneyLayout from '../components/AttorneyLayout';
import AppLayout from '../components/AppLayout';
import { getAuthToken } from '../utils/api';
import { classifyS542, type S542Figure } from '../components/settlement/texas542Display';
import { getAuthHeaders } from '../lib/authHeaders';
import { FinancialValue } from '../components/shared/FinancialValue';
import {
  parseGenerationBlock, attestationBlockBody, NOT_ATTESTED_TITLE, type GenerationBlock,
} from '../services/generationGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DocStatus = 'idle' | 'generating' | 'ready' | 'error';

interface DocState {
  status: DocStatus;
  error?: string;
  /** Set when the export was blocked by the attestation 412 gate. */
  attestationBlock?: GenerationBlock;
  pdfBlob?: Blob;
  docxBlob?: Blob;
  xlsxBlob?: Blob;
}

interface FleetInfo {
  run_id: string;
  status: string;
  created_at: string;
  p75?: number;
}

// ---------------------------------------------------------------------------
// Filing Package documents
// ---------------------------------------------------------------------------

const FILING_DOCS = [
  {
    id: 'proof_of_loss',
    label: 'Sworn Proof of Loss',
    description: 'Fleet P75 as the sworn amount',
    icon: ScrollText,
    formats: ['pdf', 'docx'] as const,
  },
  {
    id: 'demand_letter',
    label: 'Initial Demand Letter',
    description: 'Fleet P75 as demand figure + §542',
    icon: FileText,
    formats: ['pdf', 'docx'] as const,
  },
  {
    id: 'wb2',
    label: 'WB2 — BI Forensic Package',
    description: '13-tab court-ready damages workbook',
    icon: BarChart3,
    formats: ['xlsx'] as const,
  },
  {
    id: 'wb1',
    label: 'WB1 — Operating P&L',
    description: 'Pre-loss 8-tab financial workbook',
    icon: Table2,
    formats: ['xlsx'] as const,
  },
  {
    id: 'expense_worksheet',
    label: 'Continuing Expense Worksheet',
    description: 'ISO CP 00 30 expense ledger',
    icon: Receipt,
    formats: ['xlsx'] as const,
  },
  {
    id: 'court_bundle',
    label: 'Court-Ready Bundle',
    description: 'Litigation PDF: forensic report, exhibits, P&L',
    icon: Gavel,
    formats: ['pdf'] as const,
  },
] as const;

// ---------------------------------------------------------------------------
// On-Demand documents
// ---------------------------------------------------------------------------

const ONDEMAND_DOCS = [
  {
    id: 'euo_prep_memo',
    label: 'EUO Prep Memo',
    description: 'Carrier depo questions from fleet + case strategy',
    icon: Compass,
    path: 'euo-prep-memo',
  },
  {
    id: 'ror_response',
    label: 'RoR Response',
    description: 'Generate when carrier sends reservation of rights',
    icon: ShieldCheck,
    path: 'ror-response',
  },
  {
    id: 'ror_analysis',
    label: 'RoR Coverage Analysis',
    description: 'Coverage gap memo — generate with RoR response',
    icon: FileSearch,
    path: 'ror-analysis',
  },
  {
    id: 'evidence_checklist',
    label: 'Evidence Checklist',
    description: 'Attorney vulnerability + document gap checklist',
    icon: ClipboardList,
    path: 'tools?tab=evidence-checklist',
  },
] as const;

// ---------------------------------------------------------------------------
// Supporting Exports
// ---------------------------------------------------------------------------

const SUPPORTING_DOCS = [
  {
    id: 'bates',
    label: 'Bates Exhibit Package',
    description: 'Court-exhibit package with Bates numbering',
    icon: Archive,
    href: (reportId: string) => `/accountant/case/${reportId}/export`,
  },
  {
    id: 'audit_trail',
    label: 'Audit Trail',
    description: 'Hash-chained immutable reconciliation history',
    icon: BookOpen,
    href: (reportId: string) => `/accountant/case/${reportId}/export`,
  },
  {
    id: 'attorney_package',
    label: 'Attorney Package',
    description: 'Full case package: forensic + settlement + vulnerability matrix',
    icon: Package,
    href: (reportId: string) => `/attorney/case/${reportId}/tools`,
  },
  {
    id: 'defense_matrix',
    label: 'Defense Matrix',
    description: 'Carrier vulnerability counter-argument matrix',
    icon: Shield,
    href: (reportId: string) => `/attorney/case/${reportId}/tools`,
  },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CaseDocumentCenter() {
  const { caseId, claimId } = useParams<{ caseId?: string; claimId?: string }>();
  const resolvedId = caseId || claimId;
  const navigate = useNavigate();
  const Layout = caseId ? AttorneyLayout : AppLayout;

  const [caseData, setCaseData] = useState<Record<string, any>>({});
  const [fleet, setFleet] = useState<FleetInfo | null>(null);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [s542, setS542] = useState<S542Figure | null>(null);
  const [docStates, setDocStates] = useState<Record<string, DocState>>({});
  const [batchRunning, setBatchRunning] = useState(false);

  // -------------------------------------------------------------------------
  // Load case + fleet data
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!resolvedId) return;

    // Case metadata: use the attorney portal endpoint when mounted on the
    // attorney route (`/attorney/case/:caseId/documents` — `caseId` param
    // present), otherwise use the staff drafts endpoint
    // (`/case/:claimId/documents` — Clerk session handles auth either way,
    // getAuthHeaders() below supplies the bearer token for both).
    const caseUrl = caseId
      ? `${BACKEND_URL}/api/v1/attorney/case/${resolvedId}`
      : `${BACKEND_URL}/api/v1/reports/drafts/${resolvedId}`;

    getAuthHeaders()
      .then(authHeaders => {
        const caseHeaders: Record<string, string> = { 'Content-Type': 'application/json', ...authHeaders };
        return fetch(caseUrl, { headers: caseHeaders });
      })
      .then(r => r.json())
      .then(raw => {
        // Staff drafts endpoint nests user data under .data; attorney endpoint is flat
        const d = raw.data || raw;
        setCaseData({
          insured_name: d.client_name || d.insured_name || '',
          insured_address: d.insured_address || d.address || '',
          carrier_name: d.carrier_name || '',
          policy_number: d.policy_number || '',
          claim_number: d.claim_number || '',
          claim_amount: d.total_demand || d.claim_amount || 0,
          state: d.state || 'TX',
          attorney_name: d.attorney_name || '',
          attorney_firm: d.firm_name || d.attorney_firm || '',
          event_type: d.event_type || 'fire',
          event_date: d.loss_date || d.event_date || '',
          loss_period_days: d.loss_period_days || 0,
        });
      })
      .catch((e) => { console.error('CaseDocumentCenter: case metadata load failed', e); });

    setFleetLoading(true);
    getAuthToken().then(clerkTok => {
      const fleetHeaders: Record<string, string> = {};
      if (clerkTok) fleetHeaders['Authorization'] = `Bearer ${clerkTok}`;
      return fetch(`${BACKEND_URL}/api/v1/fleet/${resolvedId}/status`, { headers: fleetHeaders });
    })
      .then(r => r.json())
      .then(data => {
        if (data.status === 'completed') {
          const cards = data.cards || {};
          const forensic = (cards.forensic?.payload) || {};
          const attorney = (cards.attorney?.payload) || {};
          setFleet({
            run_id: data._id || data.run_id || '',
            status: data.status,
            created_at: data.created_at || '',
            p75: forensic.defensible_loss_range?.high,
          });
        }
      })
      .catch(() => {})
      .finally(() => setFleetLoading(false));

    // §542 comes from the one current code path (GET /texas-542), never the fleet card.
    setS542(null);
    getAuthToken().then(async tok => {
      const headers: Record<string, string> = {};
      if (tok) headers['Authorization'] = `Bearer ${tok}`;
      const r = await fetch(`${BACKEND_URL}/api/v1/texas-542/${resolvedId}`, { headers });
      let json: any = null;
      try { json = await r.json(); } catch { json = null; }
      if (r.ok && json) {
        setS542(classifyS542(json));
      } else if (r.status === 422) {
        const detail = typeof json?.detail === 'string' ? json.detail : null;
        setS542({ kind: 'not_computed', needs: detail ? [detail] : [], note: null });
      } else {
        setS542({ kind: 'unavailable', detail: null });
      }
    }).catch(() => setS542({ kind: 'unavailable', detail: null }));
  }, [resolvedId]);

  // -------------------------------------------------------------------------
  // Generate a single filing document
  // -------------------------------------------------------------------------

  const setDoc = useCallback((id: string, patch: Partial<DocState>) => {
    setDocStates(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const generateDoc = useCallback(async (docId: string) => {
    if (!resolvedId) return;

    // Clerk JWT for staff (auto-injected by app startup via setAuthTokenGetter).
    // Attorney portal users have no Clerk session so clerkToken is null — those
    // calls will 401 until attorney JWT support is added to attorney-tools endpoints.
    const clerkToken = await getAuthToken();
    const makeHeaders = (): Record<string, string> => {
      const h: Record<string, string> = { 'Content-Type': 'application/json' };
      if (clerkToken) h['Authorization'] = `Bearer ${clerkToken}`;
      return h;
    };

    // Throw with the actual server error text so we see what failed
    async function fetchBlob(url: string, opts?: RequestInit): Promise<Blob> {
      const r = await fetch(url, opts);
      if (!r.ok) {
        const msg = await r.text().catch(() => r.statusText);
        if (r.status === 412) {
          let parsed: GenerationBlock | null = null;
          try { parsed = parseGenerationBlock(JSON.parse(msg)); } catch { /* non-JSON 412 */ }
          if (parsed && parsed.kind === 'not_attested') {
            throw Object.assign(new Error(parsed.message), { attestationBlock: parsed });
          }
        }
        throw new Error(`${r.status} — ${msg.slice(0, 300)}`);
      }
      return r.blob();
    }

    setDoc(docId, { status: 'generating', error: undefined, attestationBlock: undefined });

    try {
      if (docId === 'proof_of_loss') {
        const body = {
          pol_type: 'sworn',
          case_data: caseData,
          report_id: resolvedId,
          output_format: 'text',
        };
        const opts = { method: 'POST', headers: makeHeaders(), body: JSON.stringify(body) };
        const [pdf, docx] = await Promise.all([
          fetchBlob(`${BACKEND_URL}/api/v1/attorney-tools/proof-of-loss/export/pdf`, opts),
          fetchBlob(`${BACKEND_URL}/api/v1/attorney-tools/proof-of-loss/export/docx`, opts),
        ]);
        setDoc(docId, { status: 'ready', pdfBlob: pdf, docxBlob: docx });

      } else if (docId === 'demand_letter') {
        const body = {
          letter_type: 'initial_demand',
          case_data: caseData,
          report_id: resolvedId,
          include_calculations: true,
          include_bad_faith_warning: true,
          response_deadline_days: 30,
          output_format: 'text',
        };
        const opts = { method: 'POST', headers: makeHeaders(), body: JSON.stringify(body) };
        const [pdf, docx] = await Promise.all([
          fetchBlob(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/export/pdf`, opts),
          fetchBlob(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/export/docx`, opts),
        ]);
        setDoc(docId, { status: 'ready', pdfBlob: pdf, docxBlob: docx });

      } else if (docId === 'wb2') {
        const blob = await fetchBlob(`${BACKEND_URL}/api/v1/workbook/wb2/${resolvedId}`, { headers: makeHeaders() });
        setDoc(docId, { status: 'ready', xlsxBlob: blob });

      } else if (docId === 'wb1') {
        const blob = await fetchBlob(`${BACKEND_URL}/api/v1/workbook/wb1/${resolvedId}`, { headers: makeHeaders() });
        setDoc(docId, { status: 'ready', xlsxBlob: blob });

      } else if (docId === 'expense_worksheet') {
        const blob = await fetchBlob(`${BACKEND_URL}/api/v1/expense-ledger/${resolvedId}/xlsx`, { headers: makeHeaders() });
        setDoc(docId, { status: 'ready', xlsxBlob: blob });

      } else if (docId === 'court_bundle') {
        const body = {
          documents: ['court_report', 'forensic_report', 'exhibits', 'completed_pl'],
          include_charts: false,
          include_fair_settlement: false,
          watermark: 'DRAFT',
          force_regenerate: false,
        };
        const blob = await fetchBlob(
          `${BACKEND_URL}/api/v1/download/court-ready-bundle/${resolvedId}`,
          { method: 'POST', headers: makeHeaders(), body: JSON.stringify(body) },
        );
        setDoc(docId, { status: 'ready', pdfBlob: blob });
      }
    } catch (e: any) {
      setDoc(docId, { status: 'error', error: e.message || 'Generation failed', attestationBlock: e.attestationBlock });
    }
  }, [resolvedId, caseData, setDoc]);

  // -------------------------------------------------------------------------
  // Generate all filing docs in parallel
  // -------------------------------------------------------------------------

  const generateAll = useCallback(async () => {
    setBatchRunning(true);
    await Promise.allSettled(FILING_DOCS.map(d => generateDoc(d.id)));
    setBatchRunning(false);
  }, [generateDoc]);

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const claimNumber = caseData.claim_number || resolvedId?.slice(0, 8) || 'draft';

  function DocCard({
    docId, label, description, icon: Icon,
  }: {
    docId: string;
    label: string;
    description: string;
    icon: React.ElementType;
    formats?: readonly string[];
  }) {
    const state = docStates[docId] || { status: 'idle' };

    return (
      <div
        className="flex items-start gap-4"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 16 }}
      >
        <div className="flex-shrink-0" style={{ background: 'var(--color-bg-elevated)', borderRadius: 10, padding: 8 }}>
          <Icon className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{label}</span>
            {state.status === 'ready' && <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />}
            {state.status === 'error' && <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />}
            {state.status === 'generating' && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-brand)' }} />}
          </div>
          <p className="cs-meta">{description}</p>
          {state.attestationBlock ? (
            <div
              role="alert"
              data-testid={`attestation-blocked-${docId}`}
              style={{
                marginTop: 8, padding: '8px 12px', borderRadius: 8,
                background: 'var(--color-needs-human-tint)',
                border: '1px solid var(--color-needs-human-tint-bd)',
              }}
            >
              <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>
                {state.attestationBlock.invalidated ? 'Blocked — re-attest required' : 'Blocked — not attested'}
              </p>
              <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)', marginTop: 2 }}>
                {NOT_ATTESTED_TITLE} — {attestationBlockBody(state.attestationBlock)}
              </p>
            </div>
          ) : state.error && <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)', marginTop: 4 }}>{state.error}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {state.status === 'idle' && (
            <button
              onClick={() => generateDoc(docId)}
              className="cs-chip"
              style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Generate
            </button>
          )}
          {state.status === 'generating' && (
            <span className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>Generating…</span>
          )}
          {state.status === 'ready' && (
            <div className="flex gap-1">
              {state.pdfBlob && (
                <button
                  onClick={() => downloadBlob(state.pdfBlob!, `${docId}_${claimNumber}.pdf`)}
                  className="cs-chip flex items-center gap-1"
                  style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)' }}
                >
                  <Download className="w-3 h-3" /> PDF
                </button>
              )}
              {state.docxBlob && (
                <button
                  onClick={() => downloadBlob(state.docxBlob!, `${docId}_${claimNumber}.docx`)}
                  className="cs-chip flex items-center gap-1"
                  style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)' }}
                >
                  <Download className="w-3 h-3" /> DOCX
                </button>
              )}
              {state.xlsxBlob && (
                <button
                  onClick={() => downloadBlob(state.xlsxBlob!, `${docId}_${claimNumber}.xlsx`)}
                  className="cs-chip flex items-center gap-1"
                  style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)' }}
                >
                  <Download className="w-3 h-3" /> XLSX
                </button>
              )}
              <button
                onClick={() => { setDoc(docId, { status: 'idle' }); generateDoc(docId); }}
                title="Regenerate"
                className="cs-chip"
                style={{ padding: '6px', borderRadius: 8, border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', background: 'var(--color-bg-elevated)' }}
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            </div>
          )}
          {state.status === 'error' && (
            <button
              onClick={() => generateDoc(docId)}
              className="cs-chip"
              style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const readyCount = FILING_DOCS.filter(d => docStates[d.id]?.status === 'ready').length;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8" style={{ background: 'var(--color-bg-base)', minHeight: '100%' }}>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {resolvedId && (
            <button
              onClick={() => navigate(caseId ? `/attorney/case/${resolvedId}` : `/case/${resolvedId}`)}
              className="p-1.5 rounded-lg"
              style={{ color: 'var(--color-text-muted)', background: 'var(--color-bg-elevated)' }}>
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <FolderOpen className="w-6 h-6" style={{ color: 'var(--color-text-secondary)' }} />
          <div>
            <h1 className="cs-display">Document Center</h1>
            <p className="cs-meta" style={{ marginTop: 2 }}>{caseData.insured_name || resolvedId}</p>
          </div>
        </div>

        {/* Fleet status banner */}
        {fleetLoading ? (
          <div
            className="cs-body mb-6 flex items-center gap-2"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 14, padding: '12px 16px', color: 'var(--color-text-muted)' }}
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Checking fleet run status…
          </div>
        ) : fleet ? (
          <div
            className="mb-6 flex items-center justify-between"
            style={{ background: 'transparent', border: '1px solid var(--color-verified)', borderRadius: 14, padding: '12px 16px' }}
          >
            <div className="cs-body flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
              <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
              <span>
                Fleet run complete — P75 <strong className="cs-figure" style={{ color: 'var(--color-verified)' }}><FinancialValue value={fleet.p75} /></strong>
              </span>
            </div>
            <span className="cs-meta flex items-center gap-1" style={{ color: 'var(--color-verified)' }}>
              <Clock className="w-3 h-3" />
              {fleet.created_at ? new Date(fleet.created_at).toLocaleDateString() : ''}
            </span>
          </div>
        ) : (
          <div
            className="cs-body mb-6 flex items-center gap-2"
            style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, padding: '12px 16px', color: 'var(--color-needs-human-text)' }}
          >
            <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
            No completed fleet run found. Documents will use manually entered figures.
          </div>
        )}

        {/* §542 — own line, independent of any fleet run */}
        <div className="cs-body mb-6" data-testid="s542-line" style={{ color: 'var(--color-text-secondary)' }}>
          §542 interest:{' '}
          {s542 === null ? (
            '…'
          ) : s542.kind === 'computed' ? (
            <strong className="cs-figure" style={{ color: 'var(--color-verified)' }}><FinancialValue value={s542.total} /></strong>
          ) : s542.kind === 'election' ? (
            <span style={{ color: 'var(--color-text-muted)' }}>attorney election needed</span>
          ) : s542.kind === 'not_computed' ? (
            <span style={{ color: 'var(--color-text-muted)' }} title={s542.needs.join('\n')}>
              not computed{s542.needs.length > 0 ? ` — needs ${s542.needs.length} recorded date${s542.needs.length === 1 ? '' : 's'}` : ''}
            </span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>§542 unavailable</span>
          )}
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Section 1: Filing Package */}
        {/* ------------------------------------------------------------------ */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="cs-h2">Filing Package</h2>
              <p className="cs-meta" style={{ marginTop: 2 }}>
                Core documents for initial claim filing — generated with fleet-verified figures
              </p>
            </div>
            <div className="flex items-center gap-3">
              {readyCount > 0 && (
                <span
                  className="cs-chip"
                  style={{ color: 'var(--color-verified)', background: 'transparent', border: '1px solid var(--color-verified)', borderRadius: 9999, padding: '4px 10px' }}
                >
                  {readyCount}/{FILING_DOCS.length} ready
                </span>
              )}
              <button
                onClick={generateAll}
                disabled={batchRunning}
                className="cs-body flex items-center gap-2"
                style={{ padding: '8px 16px', borderRadius: 10, background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500, opacity: batchRunning ? 0.6 : 1 }}
              >
                {batchRunning
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                  : readyCount === FILING_DOCS.length
                    ? <><RefreshCw className="w-4 h-4" /> Regenerate All</>
                    : <><FolderOpen className="w-4 h-4" /> Generate Filing Package</>
                }
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {FILING_DOCS.map(doc => (
              <DocCard
                key={doc.id}
                docId={doc.id}
                label={doc.label}
                description={doc.description}
                icon={doc.icon}
                formats={doc.formats}
              />
            ))}
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Section 2: On-Demand */}
        {/* ------------------------------------------------------------------ */}
        <section className="mb-8">
          <div className="mb-3">
            <h2 className="cs-h2">On-Demand</h2>
            <p className="cs-meta" style={{ marginTop: 2 }}>
              Generated when triggered by case events — each opens its dedicated generator
            </p>
          </div>

          <div className="space-y-2">
            {ONDEMAND_DOCS.map(doc => (
              <div
                key={doc.id}
                className="flex items-center gap-4"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 16 }}
              >
                <div className="flex-shrink-0" style={{ background: 'var(--color-bg-elevated)', borderRadius: 10, padding: 8 }}>
                  <doc.icon className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="cs-body block" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{doc.label}</span>
                  <p className="cs-meta">{doc.description}</p>
                </div>
                {resolvedId && (
                  <Link
                    to={`/attorney/case/${resolvedId}/${doc.path}`}
                    className="cs-chip flex items-center gap-1.5 flex-shrink-0"
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)' }}
                  >
                    <ExternalLink className="w-3 h-3" /> Open
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Section 3: Supporting Exports */}
        {/* ------------------------------------------------------------------ */}
        <section>
          <div className="mb-3">
            <h2 className="cs-h2">Supporting Exports</h2>
            <p className="cs-meta" style={{ marginTop: 2 }}>
              Accountant-portal exports — open in the export center
            </p>
          </div>

          <div className="space-y-2">
            {SUPPORTING_DOCS.map(doc => (
              <div
                key={doc.id}
                className="flex items-center gap-4"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 16 }}
              >
                <div className="flex-shrink-0" style={{ background: 'var(--color-bg-elevated)', borderRadius: 10, padding: 8 }}>
                  <doc.icon className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="cs-body block" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{doc.label}</span>
                  <p className="cs-meta">{doc.description}</p>
                </div>
                {resolvedId && (
                  <a
                    href={
                      (doc.id === 'attorney_package' || doc.id === 'defense_matrix')
                        ? `/attorney/case/${resolvedId}/tools`
                        : doc.href(resolvedId)
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cs-chip flex items-center gap-1.5 flex-shrink-0"
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)' }}
                  >
                    <ExternalLink className="w-3 h-3" /> Export Center
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>
    </Layout>
  );
}
